import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Button,
  Slide,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  Home as HomeIcon,
  Add as AddIcon,
  ListAlt as ListIcon,
  People as PeopleIcon,
  GetApp as InstallIcon,
  Close as CloseIcon,
  EventNote as CalendarIcon,
  Assessment as ReportsIcon,
  LocalShipping as CollectionIcon,
  MoreHoriz as MoreIcon,
} from '@mui/icons-material';
import { mobileAuthService } from '../../../services/mobile/mobileAuthService';
import { canAccessModule } from '../../../utils/userPermissions';

const navItems = [
  { label: 'Home', icon: <HomeIcon />, path: '/mobile' },
  { label: 'New', icon: <AddIcon />, path: '/mobile/new-cheque', requiredModule: 'print-cheque' },
  { label: 'Cheques', icon: <ListIcon />, path: '/mobile/cheques', requiredModule: 'print-cheque' },
  { label: 'Calendar', icon: <CalendarIcon />, path: '/mobile/pdc-calendar', requiredModule: 'pdc-calendar' },
  { label: 'More', icon: <MoreIcon />, path: null }, // opens drawer
];

const moreMenuItems = [
  { label: 'Reports', icon: <ReportsIcon />, path: '/mobile/reports', requiredModule: 'reports' },
  { label: 'Collections', icon: <CollectionIcon />, path: '/mobile/collections', requiredModule: 'cheque-collection' },
  { label: 'Beneficiaries', icon: <PeopleIcon />, path: '/mobile/beneficiaries', requiredModule: 'beneficiary-setup' },
];

// Paths that should highlight "More" in bottom nav
const moreMenuPaths = moreMenuItems.map(item => item.path);

const INSTALL_DISMISSED_KEY = 'easycheck_install_dismissed';

const MobileLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const deferredPrompt = useRef(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [permState, setPermState] = useState(null);

  useEffect(() => {
    mobileAuthService.getMyPermissions().then(setPermState);
  }, []);

  // permState null (loading/error) -> canAccessModule returns true (fail-open; backend enforces anyway)
  const visibleNavItems = navItems.filter(
    (item) => !item.requiredModule || canAccessModule(permState, item.requiredModule)
  );
  const visibleMoreItems = moreMenuItems.filter(
    (item) => !item.requiredModule || canAccessModule(permState, item.requiredModule)
  );

  useEffect(() => {
    // Don't show if already in standalone mode or previously dismissed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    const wasDismissed = localStorage.getItem(INSTALL_DISMISSED_KEY);
    if (isStandalone || wasDismissed) return;

    const handler = (e) => {
      e.preventDefault();
      deferredPrompt.current = e;
      setShowInstallBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt.current) return;
    deferredPrompt.current.prompt();
    const { outcome } = await deferredPrompt.current.userChoice;
    if (outcome === 'accepted') {
      setShowInstallBanner(false);
    }
    deferredPrompt.current = null;
  };

  const handleDismissInstall = () => {
    setShowInstallBanner(false);
    localStorage.setItem(INSTALL_DISMISSED_KEY, 'true');
  };

  const getActiveTab = () => {
    // Check main nav items first (excluding "More")
    for (let i = 0; i < visibleNavItems.length - 1; i++) {
      const item = visibleNavItems[i];
      if (item.path === '/mobile') {
        if (location.pathname === '/mobile') return i;
      } else if (location.pathname.startsWith(item.path)) {
        return i;
      }
    }
    // Check if current path is in the "More" menu
    if (moreMenuPaths.some(p => location.pathname.startsWith(p))) {
      return visibleNavItems.length - 1; // highlight "More"
    }
    return 0;
  };

  const activeTab = getActiveTab();

  const handleLogout = () => {
    mobileAuthService.logout();
    navigate('/mobile/login');
  };

  const handleNavChange = (_, newValue) => {
    const item = visibleNavItems[newValue];
    if (item.path === null) {
      // "More" tab — toggle drawer
      setMoreOpen(true);
    } else {
      setMoreOpen(false);
      navigate(item.path);
    }
  };

  const handleMoreItemClick = (path) => {
    setMoreOpen(false);
    navigate(path);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f8fafc' }}>
      {/* Top AppBar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: '#fff',
          borderBottom: '1px solid #e2e8f0',
          color: '#0f172a'
        }}
      >
        <Toolbar sx={{ minHeight: '56px !important' }}>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            <span style={{ color: '#0f172a' }}>Easy</span>
            <span style={{ color: '#2563eb' }}>Check</span>
            <Typography component="span" variant="caption" sx={{ ml: 1, color: '#94a3b8', fontWeight: 400 }}>
              Mobile
            </Typography>
          </Typography>
          <IconButton onClick={handleLogout} sx={{ color: '#64748b' }}>
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Scrollable Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          pt: '56px',
          pb: '56px',
          px: 2,
          py: 2,
          mt: '56px',
          mb: '56px'
        }}
      >
        {children}
      </Box>

      {/* Install PWA Banner */}
      <Slide direction="up" in={showInstallBanner} mountOnEnter unmountOnExit>
        <Paper
          sx={{
            position: 'fixed',
            bottom: 56,
            left: 0,
            right: 0,
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1.5,
            bgcolor: '#2563eb',
            color: '#fff',
            borderRadius: 0,
          }}
          elevation={4}
        >
          <InstallIcon fontSize="small" />
          <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 500 }}>
            Install EasyCheck for the best experience
          </Typography>
          <Button
            size="small"
            variant="contained"
            onClick={handleInstall}
            sx={{ bgcolor: '#fff', color: '#2563eb', fontWeight: 600, '&:hover': { bgcolor: '#e2e8f0' }, textTransform: 'none', minWidth: 'auto', px: 2 }}
          >
            Install
          </Button>
          <IconButton size="small" onClick={handleDismissInstall} sx={{ color: '#fff', p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Paper>
      </Slide>

      {/* More Drawer */}
      <Drawer
        anchor="bottom"
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '16px 16px 0 0',
            pb: '56px', // space for bottom nav
          }
        }}
      >
        <Box sx={{ px: 1, pt: 1.5, pb: 1 }}>
          <Box sx={{ width: 32, height: 4, borderRadius: 2, bgcolor: '#cbd5e1', mx: 'auto', mb: 1.5 }} />
          <List disablePadding>
            {visibleMoreItems.map((item) => {
              const isActive = location.pathname.startsWith(item.path);
              return (
                <ListItem key={item.path} disablePadding>
                  <ListItemButton
                    onClick={() => handleMoreItemClick(item.path)}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      bgcolor: isActive ? '#eff6ff' : 'transparent',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: isActive ? '#2563eb' : '#64748b' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontWeight: isActive ? 700 : 500,
                        color: isActive ? '#2563eb' : '#0f172a',
                        fontSize: '0.95rem',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Drawer>

      {/* Bottom Navigation */}
      <Paper
        sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1100 }}
        elevation={8}
      >
        <BottomNavigation
          value={activeTab >= 0 ? activeTab : 0}
          onChange={handleNavChange}
          sx={{
            height: 56,
            '& .Mui-selected': { color: '#2563eb' },
            '& .MuiBottomNavigationAction-root': { minWidth: 'auto', px: 0.5 },
            '& .MuiBottomNavigationAction-label': { fontSize: '0.6rem', '&.Mui-selected': { fontSize: '0.65rem' } }
          }}
        >
          {visibleNavItems.map((item) => (
            <BottomNavigationAction
              key={item.label}
              label={item.label}
              icon={item.icon}
            />
          ))}
        </BottomNavigation>
      </Paper>
    </Box>
  );
};

export default MobileLayout;
